import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { STATUS, formatRange, formatDate, groupRequests, dayLabel } from "@/lib/constants";
import { useCategories } from "@/context/CategoriesContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AvailabilityBoard, EventsCalendar, DaySummary } from "@/pages/ManagerDashboard";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { LogOut, MapPin, Users, Clock, Phone, Mail, KeyRound, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { TechnicianChat, CellCommentDialog, useUnreadMessages, ManagerMessages } from "@/components/Chat";

const ZOOMS = [0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.15, 1.3, 1.5];

function MobileZoom({ children }) {
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  const [z, setZ] = useState(() => Number(localStorage.getItem("collab-avail-zoom")) || 1);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setMobile(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  useEffect(() => { localStorage.setItem("collab-avail-zoom", String(z)); }, [z]);
  const i = ZOOMS.indexOf(z) < 0 ? 5 : ZOOMS.indexOf(z);
  if (!mobile) return children;
  return (
    <div>
      <div className="sticky top-0 z-30 flex items-center justify-end gap-1.5 mb-2 py-1 bg-slate-950/90 backdrop-blur" data-testid="collab-zoom-bar">
        <span className="text-xs text-slate-400 mr-auto">Zoom tabella</span>
        <Button size="sm" variant="outline" data-testid="collab-zoom-out" disabled={i === 0} onClick={() => setZ(ZOOMS[i - 1])} className="h-8 w-8 p-0 border-slate-700"><ZoomOut className="w-4 h-4" /></Button>
        <span className="text-xs w-11 text-center tabular-nums" data-testid="collab-zoom-value">{Math.round(z * 100)}%</span>
        <Button size="sm" variant="outline" data-testid="collab-zoom-in" disabled={i === ZOOMS.length - 1} onClick={() => setZ(ZOOMS[i + 1])} className="h-8 w-8 p-0 border-slate-700"><ZoomIn className="w-4 h-4" /></Button>
        <Button size="sm" variant="ghost" data-testid="collab-zoom-reset" onClick={() => setZ(1)} className="h-8 w-8 p-0 text-slate-400"><RotateCcw className="w-4 h-4" /></Button>
      </div>
      <div style={{ zoom: z }} data-testid="collab-zoom-area">{children}</div>
    </div>
  );
}

const dotColor = { in_attesa: "bg-amber-400", disponibile: "bg-lime-400", forse: "bg-violet-400", non_disponibile: "bg-rose-400", confermato: "bg-blue-600" };

function StatusBadge({ status }) {
  const s = STATUS[status] || { label: status, color: "bg-slate-700 text-slate-200 border-slate-600" };
  return <span data-testid={`col-status-${status}`} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${s.color}`}>{s.label}</span>;
}

export default function CollaboratorApp() {
  const { user, logout } = useAuth();
  const [chatView, setChatView] = useState("techs");
  const [events, setEvents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [unavail, setUnavail] = useState([]);
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState("events");
  const [cellMsg, setCellMsg] = useState(null);
  const unread = useUnreadMessages();

  const load = async (silent = false) => {
    try {
      const [ev, rq, tc, un] = await Promise.all([api.get("/events"), api.get("/requests"), api.get("/technicians"), api.get("/unavailability")]);
      setEvents(ev.data); setRequests(rq.data); setTechnicians(tc.data); setUnavail(un.data);
    } catch (err) { if (!silent) toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => {
    load();
    const iv = setInterval(() => load(true), 12000);
    const onVis = () => { if (document.visibilityState === "visible") load(true); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
    // eslint-disable-next-line
  }, []);

  const techById = useMemo(() => Object.fromEntries(technicians.map((t) => [t.id, t])), [technicians]);
  const noEvent = useMemo(() => requests.filter((r) => !r.event_id), [requests]);

  const reqsFor = (eid) => requests.filter((r) => r.event_id === eid);

  const detailReqs = detail ? groupRequests(reqsFor(detail.id), (r) => r.technician_id) : [];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pb-10">
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-slate-950/90 backdrop-blur-md" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="px-4 h-14 flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-2">
            <img src="/logo.webp" alt="GCAVCOM" className="h-7 w-auto" />
            <span className="text-[10px] font-bold rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 px-1.5 py-0.5">COLLABORATORE</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400 truncate max-w-[120px]">{user.name}</span>
            <ChangePasswordDialog trigger={<Button variant="ghost" size="icon" data-testid="collab-change-password-btn" title="Cambia password" className="text-slate-400 hover:text-white h-8 w-8"><KeyRound className="w-4 h-4" /></Button>} />
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn" className="text-slate-400 hover:text-white h-8 w-8"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <main className={`${view === "avail" ? "max-w-6xl" : "max-w-4xl"} mx-auto px-4 py-5 space-y-6`}>
        <div className="flex gap-2" data-testid="collab-view-switch">
          {[["events", "Eventi"], ["avail", "Disponibilità"], ["chat", `Chat${unread ? ` (${unread})` : ""}`]].map(([k, l]) => (
            <button key={k} data-testid={`collab-tab-${k}`} onClick={() => setView(k)}
              className={`rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${view === k ? "border-cyan-500 bg-cyan-500/10 text-cyan-300" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
        {view === "chat" ? (
          user?.chat_staff ? (
            <div className="space-y-3">
              <div className="flex gap-2" data-testid="collab-chat-switch">
                {[["techs", "Chat tecnici"], ["manager", "Chat con il manager"]].map(([k, l]) => (
                  <button key={k} data-testid={`collab-chat-${k}`} onClick={() => setChatView(k)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${chatView === k ? "border-cyan-500 bg-cyan-500/10 text-cyan-300" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>{l}</button>
                ))}
              </div>
              {chatView === "techs" ? <ManagerMessages events={events} staff /> : <TechnicianChat title="Chat con il manager" />}
            </div>
          ) : <TechnicianChat title="Chat con il manager" />
        ) : view === "avail" ? (<>
          <p className="text-xs text-slate-500" data-testid="collab-cell-hint">Tocca una cella per scrivere un messaggio al manager su quel tecnico e quella data.</p>
          <MobileZoom>
            <AvailabilityBoard technicians={technicians} requests={requests} events={events} unavail={unavail} sheetExport
              onCellComment={(tech, date, text) => setCellMsg({ tech, date, text })} />
          </MobileZoom>
          <CellCommentDialog cell={cellMsg} onClose={() => setCellMsg(null)} />
        </>) : (<>
        <div className="space-y-3" data-testid="collab-events-calendar">
          <EventsCalendar events={events} requests={requests} onOpen={setDetail}
            onOpenWeek={(d, e) => {
              const mon = new Date(d); mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7));
              sessionStorage.setItem("avail-highlight", e.id); sessionStorage.setItem("avail-week", mon.toISOString());
              setView("avail"); window.scrollTo(0, 0);
            }} />
          <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-xs text-slate-400 mt-4">
            {[["disponibile", "Disponibile"], ["forse", "Forse"], ["non_disponibile", "Non disponibile"], ["in_attesa", "In attesa"], ["confermato", "Confermato"]].map(([k, l]) => (
              <span key={k} className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${dotColor[k]}`} /> {l}</span>
            ))}
          </div>
        </div>

        {noEvent.length > 0 && (
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 sm:p-5" data-testid="collab-no-event">
            <h2 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-amber-400" /> Richieste senza evento</h2>
            <div className="space-y-2">
              {noEvent.map((r) => {
                const t = techById[r.technician_id];
                return (
                  <div key={r.id} data-testid={`collab-noevent-${r.id}`} className="flex items-center justify-between gap-2 flex-wrap rounded-lg border border-slate-800 bg-slate-800/40 p-2.5">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{r.title || "Disponibilità"} — {r.technician_name}</p>
                      <p className="text-xs text-slate-500">{formatRange(r.date, r.date_end)}{t?.phone ? ` · ${t.phone}` : ""}</p>
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </>)}
      </main>

      {detail && (
        <Dialog open onOpenChange={(o) => { if (!o) setDetail(null); }}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="collab-event-detail">
            <DialogHeader>
              <DialogTitle className="font-display">{detail.name}</DialogTitle>
              <DialogDescription className="text-slate-400 text-sm">
                {formatRange(detail.start_date, detail.end_date)}{detail.client_name ? ` · ${detail.client_name}` : ""}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1 text-sm text-slate-300">
              {detail.location && <p className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-cyan-400" /> {detail.location}</p>}
              {detail.notes && <p className="text-slate-400 bg-slate-800/60 rounded-lg p-2.5">{detail.notes}</p>}
            </div>
            <DaySummary event={detail} eventRequests={reqsFor(detail.id)} />
            <div className="pt-2">
              <h4 className="font-semibold mb-2 flex items-center gap-2"><Users className="w-4 h-4 text-cyan-400" /> Persone coinvolte <span className="text-xs text-slate-500">({detailReqs.length})</span></h4>
              {detailReqs.length === 0 ? (
                <p className="text-sm text-slate-500">Nessun tecnico interpellato per questo evento.</p>
              ) : (
                <div className="space-y-2">
                  {detailReqs.map((g) => {
                    const r = g[0];
                    const t = techById[r.technician_id];
                    return (
                      <div key={r.id} data-testid={`collab-person-${r.id}`} className="rounded-lg border border-slate-800 bg-slate-800/40 p-2.5">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="font-medium truncate">{r.technician_name}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {g.map((d) => (
                            <span key={d.id} data-testid={`collab-day-${d.id}`} className="inline-flex items-center gap-1.5 text-xs text-slate-300">
                              <span className={`w-2 h-2 rounded-full ${dotColor[d.status]}`} /> {dayLabel(d.date)}
                            </span>
                          ))}
                        </div>
                        {t && (t.phone || t.email) && (
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-400">
                            {t.phone && <a href={`tel:${t.phone}`} className="flex items-center gap-1 hover:text-cyan-300"><Phone className="w-3 h-3" /> {t.phone}</a>}
                            {t.email && <a href={`mailto:${t.email}`} className="flex items-center gap-1 hover:text-cyan-300"><Mail className="w-3 h-3" /> {t.email}</a>}
                          </div>
                        )}
                        {g.filter((d) => d.response_message).map((d) => <p key={d.id} className="text-xs text-violet-300 mt-1">{d.status === "forse" ? "Motivo Forse" : "Nota"} ({dayLabel(d.date)}): {d.response_message}</p>)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
